import { useEffect, useMemo, useState } from "react";
import type { CanvasNavigationTool } from "../lib/canvas-navigation-context.js";

const CONTROL =
  'input, textarea, select, button, a, summary, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="combobox"], [role="button"], [role="slider"], [role="checkbox"], [role="radio"], [role="switch"], [role="tab"], [role="listbox"], [role="menu"], [role="dialog"]';

export function useCanvasNavigation(root: HTMLElement | null) {
  const [selectedTool, selectTool] = useState<CanvasNavigationTool>("select");
  const [spaceHeld, setSpaceHeld] = useState(false);

  useEffect(() => {
    if (!root) return;
    let pointerInside = false;
    let holdingSpace = false;
    const enter = () => {
      pointerInside = true;
    };
    const leave = () => {
      pointerInside = false;
    };
    const reset = () => {
      holdingSpace = false;
      setSpaceHeld(false);
    };
    const down = (event: KeyboardEvent) => {
      if (event.code === "Space" && holdingSpace) {
        event.preventDefault();
        return;
      }
      if (
        event.code !== "Space" ||
        event.repeat ||
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      )
        return;
      const target = event.target;
      if (!(target instanceof Element) || target.closest(CONTROL)) return;
      const canvasTarget = root.contains(target);
      const unfocused =
        target === document.body || target === document.documentElement;
      if (!canvasTarget && !(pointerInside && unfocused)) return;
      event.preventDefault();
      holdingSpace = true;
      setSpaceHeld(true);
    };
    const up = (event: KeyboardEvent) => {
      if (event.code === "Space") reset();
    };
    root.addEventListener("pointerenter", enter);
    root.addEventListener("pointerleave", leave);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", reset);
    document.addEventListener("visibilitychange", reset);
    return () => {
      root.removeEventListener("pointerenter", enter);
      root.removeEventListener("pointerleave", leave);
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", reset);
      document.removeEventListener("visibilitychange", reset);
    };
  }, [root]);

  const tool = spaceHeld ? "pan" : selectedTool;
  return useMemo(() => ({ tool, selectTool }), [tool]);
}
